import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft, BookOpen, Printer, Target } from 'lucide-react';
import { toast } from 'sonner';
import PracticeScenario from '../shared/PracticeScenario';
import PageMessaging from '../shared/PageMessaging';

export default function CompassionateCommunication() {
  const navigate = useNavigate();
  const printPage = () => { window.print(); toast.success('Print dialog opened'); };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <style>{`@media print { body { background: white !important; } .no-print { display: none !important; } .print-page { background: white !important; color: black !important; } }`}</style>
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50 no-print">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/belonging-ebook')} variant="outline" size="sm" className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800"><ArrowLeft className="h-4 w-4 mr-2" />Back</Button>
              <div><h1 className="text-2xl font-bold text-white">COMPASSIONATE COMMUNICATION</h1></div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={printPage} variant="outline" size="sm" className="border-blue-600 hover:bg-blue-900/20 text-white"><Printer className="h-4 w-4 mr-2" />Print</Button>
              <div className="flex items-center gap-2 text-slate-400 text-sm"><BookOpen className="h-4 w-4" /><span>Page 72</span></div>
            </div>
          </div>
        </div>
      </div>
      <div className="container mx-auto px-6 py-12 print-page">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8"><Target className="h-16 w-16 text-green-400 mx-auto mb-4" /><h2 className="text-3xl font-bold text-white mb-4">The Goal and Objectives</h2></div>
          <div className="bg-gradient-to-br from-green-900/20 to-emerald-900/20 border border-green-700/30 rounded-lg p-8 mb-8">
            <h3 className="text-2xl font-bold text-white mb-4">The Goal</h3>
            <p className="text-green-100 leading-relaxed text-lg">The goal of Compassionate Communication is not to get our way, but rather to create a quality of connection where everyone's needs get met through compassionate giving. We become less aligned with our conflicting strategies, thoughts and judgments and we become more aligned with the endless possibilities present but unseen in every moment.</p>
          </div>
          <div className="bg-gradient-to-br from-blue-900/20 to-cyan-900/20 border border-blue-700/30 rounded-lg p-8 mb-8">
            <h3 className="text-2xl font-bold text-white mb-6">Compassionate Communication Assumes:</h3>
            <ul className="space-y-3 text-blue-100">
              <li className="flex items-start gap-3"><span className="text-blue-400 mt-1">•</span><span>Everything any person ever does is motivated by a universal human need.</span></li>
              <li className="flex items-start gap-3"><span className="text-blue-400 mt-1">•</span><span>All human beings have the same needs.</span></li>
              <li className="flex items-start gap-3"><span className="text-blue-400 mt-1">•</span><span>Our feelings exist to tell us whether or not a need has been met.</span></li>
              <li className="flex items-start gap-3"><span className="text-blue-400 mt-1">•</span><span>We differ not in our needs but in the strategies we use to meet those needs.</span></li>
              <li className="flex items-start gap-3"><span className="text-blue-400 mt-1">•</span><span>If everybody remains connected to the feelings and needs that surface during a conflict, a quality of connection will be created where "the solution will find them" and everybody's needs will be met in a way that satisfies everyone.</span></li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-purple-900/20 to-pink-900/20 border border-purple-700/30 rounded-lg p-8 mb-8">
            <h3 className="text-2xl font-bold text-white mb-6">Three Primary Objectives:</h3>
            <div className="space-y-6 text-purple-100">
              <div>
                <h4 className="text-xl font-semibold text-white mb-3">1. See Each Other's Humanness</h4>
                <p className="mb-2">Recognize that everything we or anyone ever does is because we have a need:</p>
                <ul className="space-y-1 ml-6">
                  <li>• With no enemy images</li>
                  <li>• With no moralistic judgment</li>
                  <li>• No right/wrong</li>
                  <li>• No diagnosing</li>
                </ul>
              </div>
              <div>
                <h4 className="text-xl font-semibold text-white mb-3">2. Value Others' Needs Equally</h4>
                <ul className="space-y-1 ml-6">
                  <li>• Not giving up nor giving in</li>
                  <li>• Not bullying to get our way</li>
                </ul>
              </div>
              <div>
                <h4 className="text-xl font-semibold text-white mb-3">3. Give Through Joy</h4>
                <ul className="space-y-1 ml-6">
                  <li>• Not to get a reward or avoid punishment</li>
                  <li>• Not out of obligation, guilt, or fear</li>
                  <li>• Not to "make" the other person like you or "earn" their love</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="bg-red-900/20 border border-red-700/30 rounded-lg p-6 text-center">
            <p className="text-red-200 text-xl font-bold">If your objective is to get your way, it is not Nonviolent Communication, and most likely your needs will NOT get met.</p>
          </div>
          <p className="text-center text-slate-500 text-xs mt-8">Steve Torma • The REAL Center • www.theREALcenter.org • 828-254-5613</p>
          <PracticeScenario pageTitle="COMPASSIONATE COMMUNICATION" pageNumber={72} />
          <PageMessaging pageTitle="COMPASSIONATE COMMUNICATION" pageNumber={72} />
        </div>
      </div>
    </div>
  );
}
